import { useRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContactView } from '@/services/dialer/savedListViews';
import { CONTACT_VIEWS } from '@/services/dialer/savedListViews';

/**
 * The three views of the contact list as segmented tabs (W4, B2-liste,
 * ADR-031). The view decides which population Power Mode dials, so it sits
 * in plain sight at the top of the list instead of inside the « ⋮ » menu.
 * Arrow keys move between tabs (WAI-ARIA tabs pattern).
 */
const LABEL_KEY: Record<ContactView, string> = {
  default: 'dialer:contacts.views.toCall',
  callbacks: 'dialer:contacts.views.callbacks',
  archived: 'dialer:contacts.views.archived',
};
const HINT_KEY: Record<ContactView, string> = {
  default: 'dialer:contacts.views.hint.toCall',
  callbacks: 'dialer:contacts.banner.callbacks',
  archived: 'dialer:contacts.banner.archived',
};

export function ContactViewTabs({ value, onChange, counts }: {
  value: ContactView;
  onChange: (view: ContactView) => void;
  /** null while the list loads: no count rather than a false 0. */
  counts: Record<ContactView, number> | null;
}) {
  const { t } = useTranslation(['dialer']);
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = (index + step + CONTACT_VIEWS.length) % CONTACT_VIEWS.length;
    const view = CONTACT_VIEWS[next] as ContactView;
    onChange(view);
    refs.current[next]?.focus();
  };
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <div
        role="tablist"
        aria-label={t('dialer:contacts.views.label')}
        className="inline-flex items-center rounded-vf p-0.5"
        style={{ background: 'var(--vf-surface-2)', border: '1px solid var(--vf-border)' }}
      >
        {CONTACT_VIEWS.map((view, i) => {
          const selected = view === value;
          return (
            <button
              key={view}
              ref={(el) => { refs.current[i] = el; }}
              type="button"
              role="tab"
              id={`contact-view-tab-${view}`}
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(view)}
              onKeyDown={(e) => onKeyDown(e, i)}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-vf-sm px-3 text-[13px] font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[var(--vf-accent)] motion-reduce:transition-none"
              style={{
                background: selected ? 'var(--vf-bg)' : 'transparent',
                color: selected ? 'var(--vf-text-1)' : 'var(--vf-text-3)',
                boxShadow: selected ? '0 0 0 1px var(--vf-border)' : undefined,
              }}
            >
              {t(LABEL_KEY[view])}{counts && " "}
              {counts && (
                <span className="vf-font-mono tabular-nums text-[11px]" style={{ color: selected ? 'var(--vf-accent)' : 'var(--vf-text-4)' }}>
                  {counts[view]}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p className="text-[12px]" style={{ color: 'var(--vf-text-3)' }}>{t(HINT_KEY[value])}</p>
    </div>
  );
}
